import React, { useEffect, useState } from "react";

/**
 * MultiplierField — numeric input for a tranche reward multiplier. Holds a
 * local draft string so decimals (e.g. "1.5") can be typed without the
 * trailing dot being dropped on each keystroke; resyncs when the committed
 * value changes externally (e.g. a tranche is removed).
 *
 * Generic Component: a bare decimal-filtered `<input>` with a controlled
 * numeric value. `null` = EMPTY (not yet typed) — the input renders blank
 * (showing its placeholder) instead of a seeded `0`, and clearing the field
 * emits `null`. The caller owns the surrounding field chrome and unit label.
 */
export function MultiplierField({
    value,
    onChange,
    placeholder,
    cursorKey
}: {
    value: number | null;
    onChange: (next: number | null) => void;
    /** Placeholder shown when the field is empty (`value === null`). */
    placeholder?: string;
    /** Sim-only cursor key placed on the input so a scripted cursor can travel
     *  to this specific row's multiplier field. Inert in production. The forced
     *  hover/focus VISUAL is painted by the caller's field wrapper (which owns
     *  the border chrome), so this leaf only needs the cursor target. */
    cursorKey?: string;
}): React.ReactElement {
    const [draft, setDraft] = useState(value === null ? "" : String(value));

    useEffect(() => {
        // Empty external value ⇒ blank draft; otherwise resync when the
        // committed number changes (e.g. a tranche is removed above this row).
        if (value === null) {
            if (draft !== "") setDraft("");
        } else if (Number(draft) !== value) {
            setDraft(String(value));
        }
        // Only resync on external value changes — not on local edits.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    return (
        <input
            inputMode="decimal"
            value={draft}
            placeholder={placeholder}
            data-sim-cursor-key={cursorKey}
            onChange={(e) => {
                const cleaned = e.target.value.replace(/[^0-9.]/g, "");
                setDraft(cleaned);
                onChange(cleaned === "" ? null : Number(cleaned) || 0);
            }}
            aria-label="Reward multiplier"
            className="flex-1 min-w-0 bg-transparent outline-none font-numeric text-base text-foreground-base placeholder:text-foreground-neutral"
        />
    );
}
